import React, { useCallback, useEffect, useState } from 'react';
import { FileText, ShieldCheck, Loader, AlertCircle, Check, Download, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { paymentsAPI } from '../services/PaymentApi';

/**
 * The platform fee agreement — read it, choose who funds the fee, sign it.
 *
 * Owner-agnostic like the payouts screen around it: an academy signs this in
 * Settings, a person signs the identical thing in their profile, and only the
 * owner differs.
 *
 * ─── WHY THE DOCUMENT COMES FROM THE SERVER ─────────────────────────────────
 *
 * Every binding word — the rate, the legal name, the clauses — is rendered
 * server-side and shown here verbatim. This component never composes contract
 * text, because then the contract would be whatever the client said it was. It
 * contributes the signature and nothing else.
 */

const MODE_COPY = {
  customer: {
    label: 'Add it to the customer\'s total',
    detail: 'They see a separate "Service fee" line at checkout. You receive your full price.',
  },
  split_50: {
    label: 'Split it evenly',
    detail: 'Half is added to the customer\'s total, half comes out of your share.',
  },
  absorb: {
    label: 'Take it from my share',
    detail: 'The customer pays your price exactly. The fee comes out of what you receive.',
  },
};

const money = (n, currency = 'USD') =>
  `${currency === 'USD' ? '$' : ''}${Number(n || 0).toFixed(2)}${currency === 'USD' ? '' : ` ${currency}`}`;

/**
 * @param {string}  connectState   not_connected | incomplete | pending | active
 * @param {Function} onConnect     starts Stripe onboarding, owned by the parent
 */
export default function FeeAgreementCard({ ownerType, ownerId, onSigned, connectState, onConnect }) {
  const { theme } = useTheme();
  const [state, setState]       = useState(null);   // server response
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [name, setName]         = useState('');
  const [title, setTitle]       = useState('');
  const [mode, setMode]         = useState('customer');
  const [agreed, setAgreed]     = useState(false);
  const [signing, setSigning]   = useState(false);
  const [opening, setOpening]   = useState(false);
  const [savingMode, setSavingMode] = useState(false);

  const load = useCallback(async () => {
    if (!ownerId) return;
    setLoading(true);
    try {
      const res = await paymentsAPI.getFeeAgreement(ownerType, ownerId);
      setState(res);
      if (res?.terms?.defaultFeeMode) setMode(res.terms.defaultFeeMode);
    } catch (err) {
      toast.error(err?.message || 'Could not load the agreement');
    } finally {
      setLoading(false);
    }
  }, [ownerType, ownerId]);

  useEffect(() => { load(); }, [load]);

  /** Open the signed PDF. Base64 rather than a URL, so nothing is public. */
  const openDocument = async () => {
    setOpening(true);
    try {
      const res = await paymentsAPI.getFeeAgreementDocument(ownerType, ownerId);
      const bytes = Uint8Array.from(atob(res.base64), c => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: res.contentType }));
      window.open(url, '_blank', 'noopener');
      // Revoked on a delay: revoking immediately can beat the new tab to it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err?.message || 'Could not open the agreement');
    } finally {
      setOpening(false);
    }
  };

  /**
   * Change who funds the fee, after signing.
   *
   * No re-signature: the agreed RATE is the contractual term and is frozen,
   * but who funds it is an operational preference. Making them sign again to
   * move a surcharge on and off would turn a term into a trap.
   */
  const changeMode = async (next) => {
    setSavingMode(true);
    try {
      await paymentsAPI.setFeeAgreementMode({ ownerType, ownerId, defaultFeeMode: next });
      toast.success('Saved — this applies to prices that follow the agreement');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Could not change who pays the fee');
    } finally {
      setSavingMode(false);
    }
  };

  const submit = async () => {
    if (!name.trim() || !title.trim()) { toast.error('Enter your name and role'); return; }
    if (!agreed) { toast.error('Please confirm you agree to the terms'); return; }
    setSigning(true);
    try {
      await paymentsAPI.signFeeAgreement({
        ownerType, ownerId,
        signatureName: name.trim(), signatureTitle: title.trim(), defaultFeeMode: mode,
      });
      toast.success('Agreement signed — online payments can now be switched on');
      await load();
      onSigned?.();
    } catch (err) {
      toast.error(err?.message || 'Could not sign the agreement');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className={`${theme.card} border rounded-xl p-5 flex items-center gap-2`}>
        <Loader size={16} className="animate-spin" />
        <span className={`text-sm ${theme.textMuted}`}>Loading agreement…</span>
      </div>
    );
  }
  if (!state) return null;

  // ── Already signed ────────────────────────────────────────────────────────
  if (state.state === 'signed') {
    const a = state.agreement;
    return (
      <div className={`${theme.card} border rounded-xl p-5`}>
        <div className="flex items-start gap-3">
          <ShieldCheck size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p className={`font-semibold ${theme.text}`}>Service agreement signed</p>
            <p className={`text-sm ${theme.textMuted} mt-0.5`}>
              {a.percent}% + {money(a.fixed, a.currency)} per online payment · signed by {a.signatureName} ({a.signatureTitle})
              {a.signedAt ? ` on ${new Date(a.signedAt).toLocaleDateString()}` : ''}
            </p>
            {/* Editable here rather than frozen at signature: the rate is the
                agreed term, who funds it is a preference. */}
            <div className="mt-3">
              <label className={`block text-xs font-medium ${theme.text} mb-1`}>
                Who pays the fee?
              </label>
              <select
                value={a.defaultFeeMode || 'customer'}
                disabled={savingMode}
                onChange={e => changeMode(e.target.value)}
                className={`w-full sm:max-w-sm px-3 py-2 border rounded-lg text-sm ${theme.input} focus:ring-2 focus:ring-blue-500 disabled:opacity-50`}
              >
                {Object.entries(MODE_COPY).map(([id, copy]) => (
                  <option key={id} value={id}>{copy.label}</option>
                ))}
              </select>
              <p className={`text-xs ${theme.textMuted} mt-1`}>
                {savingMode
                  ? 'Saving…'
                  : `${MODE_COPY[a.defaultFeeMode]?.detail || ''} Individual price tags can override this.`}
              </p>
            </div>

            {/* The agreement stays reachable. What you signed should not become
                unreadable the moment you sign it. */}
            <button
              onClick={openDocument}
              disabled={opening}
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline disabled:opacity-50"
            >
              <Download size={14} />
              {opening ? 'Opening…' : 'View the signed agreement'}
            </button>
          </div>
        </div>

        {/* NEXT STEP. Signing settles the terms; it does not move any money.
            Until Stripe is connected, Kynaix still collects on their behalf —
            so the half-finished state gets a name and a button rather than
            leaving someone to wonder what they just achieved. */}
        {connectState && connectState !== 'active' && (
          <div className={`mt-4 pt-4 border-t ${theme.border} flex items-start gap-3`}>
            <ArrowRight size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-semibold ${theme.text}`}>
                Next: connect your Stripe account
              </p>
              <p className={`text-xs ${theme.textMuted} mt-0.5`}>
                {connectState === 'incomplete' || connectState === 'pending'
                  ? 'Your setup was started but not finished. Until it is, Kynaix keeps collecting on your behalf.'
                  : 'Until you do, Kynaix collects your payments and you are paid separately.'}
              </p>
              {onConnect && (
                <button
                  onClick={onConnect}
                  className={`mt-2.5 px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-sm`}
                >
                  {connectState === 'not_connected' ? 'Connect Stripe' : 'Finish connecting'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Not signed yet ────────────────────────────────────────────────────────
  const { terms, document: doc } = state;

  return (
    <div className={`${theme.card} border rounded-xl overflow-hidden`}>
      <div className="p-5">
        <div className="flex items-start gap-3">
          <FileText size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className={`font-semibold ${theme.text}`}>Service agreement required for online payments</p>
            <p className={`text-sm ${theme.textMuted} mt-0.5`}>
              Kynaix keeps <span className={`font-semibold ${theme.text}`}>
                {terms.percent}% + {money(terms.fixed, terms.currency)}
              </span> per online payment. Nothing is charged until you sign.
            </p>
          </div>
        </div>

        {/* The plain-English version, straight from the template. */}
        {doc?.summary && (
          <ul className={`mt-4 space-y-1.5 text-sm ${theme.textSecondary}`}>
            {doc.summary.points.map((p, i) => (
              <li key={i} className="flex items-start gap-2">
                <Check size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        )}

        <button
          onClick={() => setExpanded(v => !v)}
          className={`mt-3 text-sm underline ${theme.textMuted}`}
        >
          {expanded ? 'Hide the full agreement' : 'Read the full agreement'}
        </button>
      </div>

      {expanded && (
        <div className={`px-5 pb-5 max-h-80 overflow-y-auto border-t ${theme.border} pt-4 space-y-3`}>
          <p className={`font-semibold ${theme.text}`}>{doc.title}</p>
          {doc.sections?.map((s, i) => (
            <div key={i}>
              <p className={`text-sm font-semibold ${theme.text}`}>{s.heading}</p>
              <p className={`text-sm ${theme.textSecondary} mt-0.5`}>{s.body}</p>
            </div>
          ))}
        </div>
      )}

      <div className={`px-5 py-4 border-t ${theme.border} space-y-4`}>
        {/* Who funds the fee. Asked once here rather than on every price tag,
            because it is a policy the host holds — the per-price override
            exists for the exception. */}
        <div>
          <p className={`text-sm font-medium ${theme.text} mb-2`}>Who pays the fee?</p>
          <div className="space-y-2">
            {Object.entries(MODE_COPY).map(([id, copy]) => (
              <label
                key={id}
                className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                  mode === id ? 'border-blue-500 bg-blue-500/5' : theme.border
                }`}
              >
                <input
                  type="radio" name="feeMode" value={id}
                  checked={mode === id} onChange={() => setMode(id)}
                  className="mt-1"
                />
                <span className="min-w-0">
                  <span className={`block text-sm font-medium ${theme.text}`}>{copy.label}</span>
                  <span className={`block text-xs ${theme.textMuted}`}>{copy.detail}</span>
                </span>
              </label>
            ))}
          </div>
          <p className={`text-xs ${theme.textMuted} mt-2`}>
            You can change this per price tag later.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={`block text-xs font-medium ${theme.text} mb-1`}>Full name</label>
            <input
              value={name} onChange={e => setName(e.target.value)}
              placeholder="Your full name"
              className={`w-full px-3 py-2 border rounded-lg text-sm ${theme.input} focus:ring-2 focus:ring-blue-500`}
            />
          </div>
          <div>
            <label className={`block text-xs font-medium ${theme.text} mb-1`}>Your role</label>
            <input
              value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Owner"
              className={`w-full px-3 py-2 border rounded-lg text-sm ${theme.input} focus:ring-2 focus:ring-blue-500`}
            />
          </div>
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
            className="mt-0.5"
          />
          <span className={`text-xs ${theme.textSecondary}`}>
            {doc?.signature?.consentText
              || 'I agree to the terms above and confirm I am authorised to do so.'}
          </span>
        </label>

        <div className="flex items-center gap-2">
          <button
            onClick={submit}
            disabled={signing || !agreed || !name.trim() || !title.trim()}
            className={`px-4 py-2 ${theme.buttonPrimary} rounded-lg text-sm disabled:opacity-50`}
          >
            {signing ? 'Signing…' : 'Agree and sign'}
          </button>
          <span className={`text-xs ${theme.textMuted} flex items-center gap-1`}>
            <AlertCircle size={12} /> Required before online payments can be switched on
          </span>
        </div>
      </div>
    </div>
  );
}
